import {
  PricingEngine,
  type SelectedAddOn,
  type SelectedServiceItem,
  type Service,
} from '../pricing';
import { createOrderAtomically } from '../../db/repositories';
import type { OrderItemRecord, PaymentMethod } from '../../db/types';
import { db } from '../../db/database';
import { customerCode, normalizePhilippineMobile } from '../customers/customerUtils';

export type LocalOrderLine = {
  service: Service;
  quantity: number;
  weightKg?: number;
  items: SelectedServiceItem[];
  addOns: SelectedAddOn[];
};

export type CompleteLocalOrderInput = {
  lines: LocalOrderLine[];
  customer?: { id?: string; name: string; mobileNumber?: string; address?: string; notes?: string };
  payment: { method: PaymentMethod; amountCentavos: number; reference?: string };
  cashierId?: string;
  deviceId?: string;
};

async function nextOrderNumber(): Promise<string> {
  const parts = new Intl.DateTimeFormat('en-PH', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const datePart = `${parts.find((part) => part.type === 'year')?.value ?? ''}${parts.find((part) => part.type === 'month')?.value ?? ''}${parts.find((part) => part.type === 'day')?.value ?? ''}`;
  const prefix = `BBS-${datePart}-`;
  const existing = await db.orders.where('orderNumber').startsWith(prefix).toArray();
  const used = new Set(
    existing.map((order) => Number(order.orderNumber.slice(prefix.length))).filter(Number.isFinite),
  );
  let sequence = 1;
  while (used.has(sequence)) sequence += 1;
  return `${prefix}${String(sequence).padStart(4, '0')}`;
}

async function nextClaimCode(): Promise<string> {
  const existing = new Set(
    (await db.orders.toArray()).map((order) => order.claimCode).filter(Boolean),
  );
  for (let attempt = 0; attempt < 10000; attempt += 1) {
    const code = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    if (!existing.has(code)) return code;
  }
  throw new Error('No available claim code remains on this device.');
}

export async function completeLocalOrder(input: CompleteLocalOrderInput) {
  if (input.lines.length === 0)
    throw new Error('Add at least one service before completing the order.');

  const lines = input.lines.map((line) => ({
    ...line,
    addOns: PricingEngine.normalizeAddOns(line.addOns),
  }));
  const calculated = lines.map((line) =>
    PricingEngine.calculate({
      service: line.service,
      weightKg: line.weightKg,
      quantity: line.quantity,
      items: line.items,
      addOns: line.addOns,
    }),
  );
  const subtotalCentavos = calculated.reduce((sum, result) => sum + result.subtotalCentavos, 0);
  const totalCentavos = calculated.reduce((sum, result) => sum + result.grandTotalCentavos, 0);
  if (input.payment.amountCentavos < totalCentavos)
    throw new Error('Payment is less than the amount due.');
  if (
    (input.payment.method === 'GCASH' || input.payment.method === 'MAYA') &&
    !input.payment.reference?.trim()
  ) {
    throw new Error('Payment reference is required for GCash or Maya.');
  }
  if (
    (input.payment.method === 'GCASH' || input.payment.method === 'MAYA') &&
    input.payment.amountCentavos !== totalCentavos
  ) {
    throw new Error('Digital payment amount must equal the amount due.');
  }

  const now = new Date().toISOString();
  const orderId = crypto.randomUUID();
  const idempotencyKey = crypto.randomUUID();
  const orderNumber = await nextOrderNumber();
  const claimCode = await nextClaimCode();
  const customerId = input.customer?.name.trim()
    ? (input.customer.id ?? crypto.randomUUID())
    : undefined;
  const existingCustomer = customerId ? await db.customers.get(customerId) : undefined;
  const customer =
    customerId && input.customer
      ? {
          ...input.customer,
          id: customerId,
          customerCode: existingCustomer?.customerCode ?? customerCode(),
          mobileNumber: normalizePhilippineMobile(input.customer.mobileNumber),
          createdAt: existingCustomer?.createdAt ?? now,
          updatedAt: now,
        }
      : undefined;

  const items: OrderItemRecord[] = [];
  lines.forEach((line, index) => {
    const result = calculated[index];
    if (!result) throw new Error('Unable to calculate the order line.');
    const addonSelections = PricingEngine.createAddOnSnapshots({
      service: line.service,
      weightKg: line.weightKg,
      quantity: line.quantity,
      items: line.items,
      addOns: line.addOns,
    });
    if (line.service.pricingType === 'PER_ITEM') {
      line.items.forEach((selection, itemIndex) => {
        const itemLineTotal = selection.item.unitPriceCentavos * selection.quantity;
        const addonShare = itemIndex === 0 ? result.addOnsSubtotalCentavos : 0;
        const ownsAddOns = itemIndex === 0;
        items.push({
          id: crypto.randomUUID(),
          orderId,
          serviceId: line.service.id,
          serviceName: line.service.name,
          servicePricingType: line.service.pricingType,
          serviceItemId: selection.item.id,
          serviceItemName: selection.item.name,
          addOnIds: ownsAddOns ? addonSelections.map((selection) => selection.addonId) : [],
          addOnNames: ownsAddOns ? addonSelections.map((selection) => selection.nameSnapshot) : [],
          addonSelections: ownsAddOns ? addonSelections : [],
          quantity: selection.quantity,
          weightKg: line.weightKg,
          unitPriceCentavos: selection.item.unitPriceCentavos,
          addOnsTotalCentavos: addonShare,
          lineTotalCentavos: itemLineTotal + addonShare,
          createdAt: now,
        });
      });
    } else {
      const unitPrice =
        line.service.pricingType === 'PER_KG'
          ? line.service.priceCentavos
          : line.service.priceCentavos;
      items.push({
        id: crypto.randomUUID(),
        orderId,
        serviceId: line.service.id,
        serviceName: line.service.name,
        servicePricingType: line.service.pricingType,
        addOnIds: addonSelections.map((selection) => selection.addonId),
        addOnNames: addonSelections.map((selection) => selection.nameSnapshot),
        addonSelections,
        quantity: line.quantity,
        weightKg: line.weightKg,
        unitPriceCentavos: unitPrice,
        addOnsTotalCentavos: result.addOnsSubtotalCentavos,
        lineTotalCentavos: result.grandTotalCentavos,
        createdAt: now,
      });
    }
  });

  const order = {
    id: orderId,
    orderNumber,
    claimCode,
    customerId,
    deviceId: input.deviceId ?? 'device-local',
    cashierId: input.cashierId ?? 'cashier-local',
    subtotalCentavos,
    discountCentavos: 0,
    totalCentavos,
    paymentStatus: 'PAID' as const,
    orderStatus: 'RECEIVED' as const,
    idempotencyKey,
    createdAt: now,
    updatedAt: now,
  };
  const payment = {
    id: crypto.randomUUID(),
    orderId,
    method: input.payment.method,
    amountCentavos: input.payment.amountCentavos,
    reference: input.payment.reference?.trim() || undefined,
    createdAt: now,
  };
  const audit = {
    id: crypto.randomUUID(),
    action: 'ORDER_CREATED' as const,
    entityType: 'ORDER' as const,
    entityId: orderId,
    actorId: input.cashierId ?? 'cashier-local',
    deviceId: input.deviceId ?? 'device-local',
    metadata: JSON.stringify({
      orderNumber,
      claimCode,
      paymentMethod: input.payment.method,
      offline: true,
    }),
    createdAt: now,
  };
  const outbox = {
    id: crypto.randomUUID(),
    eventType: 'ORDER_CREATED',
    aggregateType: 'ORDER' as const,
    aggregateId: orderId,
    idempotencyKey,
    payload: JSON.stringify({ order, items, customer, payment }),
    status: 'PENDING' as const,
    attempts: 0,
    createdAt: now,
    updatedAt: now,
  };

  await createOrderAtomically({ order, items, customer, payment, audit, outbox });
  return { order, customer, payment, changeCentavos: input.payment.amountCentavos - totalCentavos };
}
